import { useQuery } from "@apollo/client";
import {
  Badge,
  BaseTable,
  Button,
  Sentiments,
  Sizes,
  Variants
} from "@sede-x/shell-ds-react-framework";
import {
  CrossCircle,
  EditOutlined,
  Search
} from "@sede-x/shell-ds-react-framework/build/esm/components/Icon/components";
import {
  ColumnDef,
  SortingState,
  getPaginationRowModel,
  getSortedRowModel
} from "@tanstack/react-table";
import { MackPrivilege } from "auth";
import { useMackAuth } from "auth/AuthenticationProvider";
import { ROWS_PER_PAGE } from "carbonIQ/CarbonIQConstants";
import { loader } from "graphql.macro";
import { useMemo, useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import ColumnText from "shared/components/basetable/cells/ColumnText";
import NoDataTableImage from "shared/components/basetable/NoDataTableImage";
import GlobalHeader from "shared/components/GlobalHeader";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse } from "types";
import { TCICustomer, TCustomerFilter } from "../carbonIQtypes";
import CustomerLookup from "./CustomerLookup";
import UpdateCustomer from "./UpdateCustomer";

const carbonCustomers = loader("./../graphql/carbonCustomers.graphql");

type TCustomerSearchResponse = GqlResponse<TCICustomer[], "ciCustomersFilterBy">;

const gridColumns: ColumnDef<Partial<TCICustomer>>[] = [
  {
    header: "Customer Name",
    accessorKey: "gsapName",
    enableSorting: true,
    cell: arg => <ColumnText width={"225px"}>{arg.getValue() as string}</ColumnText>
  },
  {
    header: "Display Name",
    accessorKey: "displayName",
    enableSorting: true,
    cell: arg => <ColumnText width={"225px"}>{arg.getValue() as string}</ColumnText>
  },
  {
    header: "Customer Email",
    accessorKey: "email",
    enableSorting: true,
    cell: arg => <ColumnText width={"225px"}>{arg.getValue() as string}</ColumnText>
  },
  {
    header: "Key A/c Mgr Email",
    accessorKey: "managerEmail",
    enableSorting: true,
    cell: arg => <ColumnText width={"225px"}>{arg.getValue() as string}</ColumnText>
  },
  {
    header: "Generate Letters?",
    accessorKey: "shouldGenerateLetters",
    enableSorting: true,
    cell: arg => <ColumnText width={"75px"}>{arg.getValue() as string}</ColumnText>
  }
];

const gridColumnsGenerate = (
  clickcallback: (arg: {
    row: {
      original: Partial<TCICustomer>;
    };
  }) => void,
  editSelected: number | undefined,
  isDisabled: boolean
) => {
  if (isDisabled) {
    return gridColumns;
  } else {
    const additionalGridColumn = {
      header: "",
      accessorKey: "components",
      enableSorting: false,
      cell: (arg: { row: { original: Partial<TCICustomer> } }) => (
        <Button
          className={(arg.row.original.id as number) === editSelected ? "editedRow" : ""}
          title="Edit"
          size={Sizes.Small}
          disabled={isDisabled}
          onClick={() => clickcallback(arg)}>
          <EditOutlined />
          Edit
        </Button>
      )
    };
    return [...gridColumns, additionalGridColumn];
  }
};

const CarbonIQCustomers = () => {
  const { mackUser } = useMackAuth();
  const [loading] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateOpenCustomerDetails, setUpdateOpenCustomerDetails] =
    useState<Partial<TCICustomer> | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedCustomerSearchFilter, setSelectedCustomerSearchFilter] = useState<
    Partial<TCustomerFilter>
  >({});
  const [editSelected, setEditSelected] = useState<number>();
  const [originalData, setOriginalData] = useState<TCICustomer[]>([]);

  const {
    loading: ciqCustomersLoading,
    error: ciqCustomersError,
    data: customersData,
    refetch
  } = useQuery<TCustomerSearchResponse>(carbonCustomers, {
    fetchPolicy: "no-cache",
    onCompleted: data => {
      setOriginalData(data?.ciCustomersFilterBy || []);
      setPagination({
        pageIndex: 0,
        pageSize: ROWS_PER_PAGE
      });
    }
  });

  const columns = gridColumnsGenerate(
    arg => {
      setEditSelected(arg.row.original.id as number);
      setUpdateOpenCustomerDetails(arg.row.original);
      setUpdateOpen(true);
    },
    editSelected,
    !mackUser?.hasPrivilege(MackPrivilege.ModifyCICustomer)
  );

  const [sorting, setSorting] = useState<SortingState>([{ desc: false, id: "gsapName" }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: ROWS_PER_PAGE //default page size
  });

  const tableOptions = {
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel()
  };

  const isLoading = [ciqCustomersLoading, loading].some(l => l);
  const handleSearchSubmit = (data: TCustomerFilter) => {
    setSelectedCustomerSearchFilter(data);
    if (data?.customerIDs && data?.customerIDs?.length === 0) {
      return; //user has clicked search without first selecting customer(s)
    }
    setSearchOpen(false);
  };
  const handleUpdateSubmit = () => {
    setUpdateOpen(false); // Close the modal first for better UX
    refetch({
      fetchPolicy: "network-only"
    }).then(response => {
      if (response?.data?.ciCustomersFilterBy) {
        setOriginalData(response.data.ciCustomersFilterBy);
      }
    });
  };

  const handleCustomerSearchReset = () => {
    setSelectedCustomerSearchFilter({});
    setPagination({
      pageIndex: 0,
      pageSize: ROWS_PER_PAGE
    });
  };

  const filteredCustomers = useMemo(() => {
    // If no filter selected, return all customers
    if (!selectedCustomerSearchFilter?.customerIDs?.length) {
      return originalData;
    }

    // Otherwise, filter the customers by ID
    const selectedIds = selectedCustomerSearchFilter.customerIDs.map(c => c.id);
    return originalData.filter(customer => selectedIds.includes(customer.id));
  }, [originalData, selectedCustomerSearchFilter]);
  const isError = [ciqCustomersError].some(e => e);

  return (
    <>
      {isError && <ApolloErrorViewer error={ciqCustomersError} />}

      {isLoading && <LoadingPanel />}

      <GlobalHeader
        pageName="Customers"
        filterButtonContent={[
          selectedCustomerSearchFilter &&
            Object.keys(selectedCustomerSearchFilter).length > 0 && (
              <span className="customer-Search-current-filter">
                {!!selectedCustomerSearchFilter?.customerIDs?.length && (
                  <span className="customer-Search-current-filter-txt">
                    {selectedCustomerSearchFilter?.customerIDs
                      ?.map(elm => elm.label)
                      .join(", ")}
                  </span>
                )}
                {!!selectedCustomerSearchFilter?.customerIDs?.length && (
                  <button className="customer-Search-reset" onClick={handleCustomerSearchReset}>
                    <CrossCircle />
                    Clear Filter
                  </button>
                )}
              </span>
            )
        ]}
        descriptiontxt="Please use this screen to change the Display name for any customer, their email and that of their Key Account Manager. You can also decide if you want to stop letters being generated for any customer."
        buttonContent={[
          <Badge
            key={1}
            icon={<Search />}
            sentiment={Sentiments.Information}
            variant={Variants.Filled}
            onClick={() => setSearchOpen(!searchOpen)}
          />
        ]}
      />

      <BaseTable
        columns={columns}
        data={filteredCustomers}
        className="carboniq-data-table"
        tableOptions={tableOptions}
        emptyStateProps={{ image: <NoDataTableImage /> }}
      />

      {searchOpen && (
        <CustomerLookup
          customerData={originalData}
          onClose={() => setSearchOpen(!searchOpen)}
          onSubmit={handleSearchSubmit}
        />
      )}

      {updateOpen && (
        <UpdateCustomer
          onClose={() => setUpdateOpen(!updateOpen)}
          onSubmit={handleUpdateSubmit}
          details={updateOpenCustomerDetails}
        />
      )}
    </>
  );
};

export default CarbonIQCustomers;
